import type { GoalDto } from "@home/shared";
import { useState } from "react";
import { api } from "../api.ts";
import { money } from "../format.ts";
import { t, type Lang } from "../i18n.ts";
import { errorText, useMeta } from "../meta.ts";
import { useSync } from "../store.ts";
import { hapticSelect, hapticTap } from "../telegram.ts";
import { invalidateRemote } from "../useRemote.ts";
import { parseAmount } from "../amount.ts";
import { AmountField, type Currency } from "./AmountField.tsx";
import { EmptyState } from "./EmptyState.tsx";
import { Segmented } from "./Segmented.tsx";
import { PrimaryAction, Sheet } from "./Sheet.tsx";
import { toast } from "./Toast.tsx";

const QUICK_AMOUNTS: Record<Currency, number[]> = { UZS: [50_000, 100_000, 200_000, 500_000], USD: [10, 50, 100, 200] };

function percent(g: GoalDto): number {
  return g.target > 0 ? Math.min(100, Math.round((g.saved / g.target) * 100)) : 0;
}

function deadlineText(deadline: string): string {
  return t("until", { date: `${deadline.slice(8, 10)}.${deadline.slice(5, 7)}.${deadline.slice(0, 4)}` });
}

/** Копилки с прогрессом; нажатие — пополнить или снять. hint — подсказка под копилкой (сколько недель копить). */
export function GoalList({
  goals,
  lang,
  onPick,
  hint,
}: {
  goals: GoalDto[];
  lang: Lang;
  onPick: (g: GoalDto) => void;
  hint?: (g: GoalDto) => string | null;
}) {
  return (
    <ul className="goal-list">
      {goals.map((g) => {
        const pct = percent(g);
        const note = pct < 100 ? hint?.(g) : null;
        return (
          <li key={g.id}>
            <button type="button" className="goal-btn" onClick={() => onPick(g)}>
              <div className="goal-line">
                <span className="goal-name">
                  {pct >= 100 ? "🎉 " : ""}
                  {g.name}
                  {g.ownerName && <span className="muted"> · {g.ownerName}</span>}
                </span>
                <b>{pct}%</b>
              </div>
              <span className="progress goal-progress">
                <span style={{ width: `${pct}%` }} />
              </span>
              <div className="goal-meta muted">
                {money(g.saved, g.currency, lang)} / {money(g.target, g.currency, lang)}
                {g.deadline && ` · ${deadlineText(g.deadline)}`}
              </div>
              {note && <div className="goal-hint">{note}</div>}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/** Карточка «Копилки» для обзора и вкладки ребёнка: список, «＋», пустое состояние с кнопкой. */
export function GoalsCard({ goals, lang }: { goals: GoalDto[]; lang: Lang }) {
  const { me } = useSync();
  const [creating, setCreating] = useState(false);
  const [picked, setPicked] = useState<GoalDto | null>(null);
  const child = me?.role === "child";
  return (
    <section className="dash-card">
      <div className="dash-card-head">
        <h3>🐷 {t("goalsTab")}</h3>
        {goals.length > 0 && (
          <button type="button" className="btn-small-ghost" onClick={() => setCreating(true)}>
            ＋ {t("newGoal")}
          </button>
        )}
      </div>
      {goals.length === 0 ? (
        <EmptyState
          icon="🐷"
          title={t("noGoalsYet")}
          text={child ? t("noGoalsChild") : t("noGoalsHint")}
          action={{ label: t("createGoal"), onClick: () => setCreating(true) }}
        />
      ) : (
        <GoalList goals={goals} lang={lang} onPick={setPicked} />
      )}
      {creating && <NewGoalSheet lang={lang} onClose={() => setCreating(false)} />}
      {picked && <ContributeSheet goal={picked} lang={lang} onClose={() => setPicked(null)} />}
    </section>
  );
}

export function NewGoalSheet({ lang, onClose }: { lang: Lang; onClose: () => void }) {
  const { me } = useSync();
  const meta = useMeta();
  const child = me?.role === "child";
  const [name, setName] = useState("");
  const [target, setTarget] = useState("");
  const [currency, setCurrency] = useState<Currency>("UZS");
  const [deadline, setDeadline] = useState("");
  const [owner, setOwner] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Как в боте: семейная, своя или копилка ребёнка.
  const owners = [
    { id: null, label: `👨‍👩‍👧 ${t("familyGoal")}` },
    ...(me ? [{ id: me.userId, label: `🙋 ${t("myGoal")}` }] : []),
    ...(meta.data?.members ?? []).filter((m) => m.role === "child" && !m.isMe).map((m) => ({ id: m.userId, label: `🧒 ${m.name}` })),
  ];
  // Ребёнок копит из кошелька, а он в сумах: детская копилка — только в сумах.
  const forChild = child || (meta.data?.members ?? []).some((m) => m.userId === owner && m.role === "child");
  const goalCurrency: Currency = forChild ? "UZS" : currency;

  const submit = async () => {
    if (busy) return;
    const value = parseAmount(target);
    if (!name.trim()) return setError(t("fillName"));
    if (!value) return setError(t("fillAmount"));
    setBusy(true);
    setError(null);
    try {
      await api.createGoal({ name: name.trim(), target: value, currency: goalCurrency, deadline: deadline || null, ownerUserId: child ? null : owner });
      hapticTap();
      toast(t("savedOk"));
      invalidateRemote();
      onClose();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };

  return (
    <Sheet title={t("newGoal")} onClose={onClose}>
      <div className="form">
        <label className="field">
          <span>{t("goalName")}</span>
          <input className="input" placeholder={t("goalNamePh")} value={name} maxLength={60} onChange={(e) => setName(e.target.value)} />
        </label>
        <AmountField
          value={target}
          onChange={setTarget}
          currency={goalCurrency}
          onCurrency={forChild ? undefined : setCurrency}
          lang={lang}
          label={t("goalTarget")}
        />
        <label className="field">
          <span>{t("deadlineOpt")}</span>
          <input className="input" type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} />
        </label>
        {!child && (
          <div className="field">
            <span>{t("goalOwner")}</span>
            <div className="chips" role="radiogroup" aria-label={t("goalOwner")}>
              {owners.map((o) => (
                <button
                  key={o.id ?? "family"}
                  type="button"
                  role="radio"
                  aria-checked={owner === o.id}
                  className={owner === o.id ? "chip active" : "chip"}
                  onClick={() => {
                    hapticSelect();
                    setOwner(o.id);
                  }}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
        )}
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <PrimaryAction label={t("createGoal")} onClick={() => void submit()} busy={busy} />
      </div>
    </Sheet>
  );
}

export function ContributeSheet({ goal, lang, onClose }: { goal: GoalDto; lang: Lang; onClose: () => void }) {
  const [direction, setDirection] = useState<"in" | "out">("in");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pct = percent(goal);
  const left = Math.max(goal.target - goal.saved, 0);

  const submit = async () => {
    if (busy) return;
    const value = parseAmount(amount);
    if (!value) return setError(t("fillAmount"));
    setBusy(true);
    setError(null);
    try {
      const res = await api.contribute(goal.id, direction === "in" ? value : -value);
      hapticTap();
      toast(res.achieved && direction === "in" ? t("goalReached") : t("savedOk"));
      invalidateRemote();
      onClose();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };

  return (
    <Sheet title={`🐷 ${goal.name}`} onClose={onClose}>
      <div className="form">
        <div className="goal-summary">
          <div className="goal-line">
            <b>{money(goal.saved, goal.currency, lang)}</b>
            <span className="muted">
              {pct}% · {money(goal.target, goal.currency, lang)}
            </span>
          </div>
          <span className="progress goal-progress">
            <span style={{ width: `${pct}%` }} />
          </span>
          <div className="goal-meta muted">
            {left > 0 ? t("left", { amount: money(left, goal.currency, lang) }) : t("goalReached")}
            {goal.deadline && ` · ${deadlineText(goal.deadline)}`}
          </div>
        </div>
        {goal.saved > 0 && (
          <Segmented
            value={direction}
            onChange={setDirection}
            options={[
              { value: "in", label: `＋ ${t("topUp")}` },
              { value: "out", label: `− ${t("withdraw")}` },
            ]}
          />
        )}
        <AmountField value={amount} onChange={setAmount} currency={goal.currency} lang={lang} quick={direction === "in" ? QUICK_AMOUNTS[goal.currency] : undefined} />
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <PrimaryAction label={direction === "in" ? t("topUp") : t("withdraw")} onClick={() => void submit()} busy={busy} />
      </div>
    </Sheet>
  );
}
